import React, { useEffect, useState } from "react";
import {
  Server, Shield, AlertTriangle, RefreshCw, Activity, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { validatorsApi, formatApiError } from "../../lib/ledgerApi";

const STATUS_OPTIONS = [
  { key: "online", label: "Online" },
  { key: "offline", label: "Offline" },
  { key: "byzantine", label: "Byzantine (DEV)" },
];

export default function AdminValidators() {
  const [data, setData] = useState({ validators: [], consensus: null });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [recent, setRecent] = useState([]);

  const load = async () => {
    try {
      const [m, c] = await Promise.all([
        validatorsApi.adminMetrics(),
        validatorsApi.consensusMetrics(),
      ]);
      setData(m);
      setRecent(c.recent_blocks || []);
    } catch (e) {
      toast.error("Load failed", { description: formatApiError(e.response?.data?.detail) });
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 5000);
    return () => clearInterval(i);
  }, []);

  const onSetStatus = async (id, status) => {
    setBusy(id);
    try {
      await validatorsApi.setStatus(id, status);
      toast.success(`Validator → ${status.toUpperCase()}`);
      await load();
    } catch (e) {
      toast.error("Update failed", { description: formatApiError(e.response?.data?.detail) });
    } finally { setBusy(null); }
  };

  return (
    <div className="max-w-6xl px-6 md:px-10 py-10 md:py-14" data-testid="admin-validators-page">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">
            / Admin · Validators
          </div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-medium flex items-center gap-3">
            <Server className="w-7 h-7 text-muted-foreground" strokeWidth={1.5} />
            Validator federation
          </h1>
          <p className="mt-3 text-sm text-muted-foreground max-w-xl">
            Toggle validator status to simulate offline / byzantine modes and
            observe consensus behaviour live. <code className="font-mono">byzantine</code> requires <code className="font-mono">DEV_MODE=true</code>.
          </p>
        </div>
        <Button variant="outline" onClick={load} className="rounded-sm">
          <RefreshCw className="w-3 h-3 mr-1.5" strokeWidth={1.5} /> Refresh
        </Button>
      </div>

      {data.consensus && (
        <div className="mb-6 grid grid-cols-2 md:grid-cols-5 gap-3 text-xs font-mono">
          <KV label="Committed blocks" value={data.consensus.committed_blocks} />
          <KV label="Stalled rounds" value={data.consensus.stalled_rounds} highlight={data.consensus.stalled_rounds > 0 ? "warn" : ""} />
          <KV label="Byzantine events" value={data.consensus.byzantine_events} highlight={data.consensus.byzantine_events > 0 ? "warn" : ""} />
          <KV label="Round p50 (ms)" value={data.consensus.round_duration_ms_p50} />
          <KV label="Round p95 (ms)" value={data.consensus.round_duration_ms_p95} />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {data.validators.map((v) => (
          <div
            key={v.id}
            data-testid={`admin-validator-${v.name}`}
            className="border border-border rounded-sm bg-card p-5"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                <div className="font-medium">{v.name}</div>
              </div>
              <StatusPill s={v.status} />
            </div>
            <div className="font-mono text-[10px] text-muted-foreground truncate mb-3" title={v.pubkey}>
              {v.pubkey?.slice(0, 24)}…
            </div>
            <div className="grid grid-cols-3 gap-2 text-[11px] font-mono mb-3">
              <KV mini label="Weight" value={v.weight} />
              <KV mini label="Signed" value={v.blocks_signed_count} />
              <KV mini label="Missed" value={v.blocks_missed_count} />
              <KV mini label="Proposed" value={v.blocks_proposed_count} />
              <KV mini label="Participation" value={`${v.participation_pct ?? 0}%`} />
            </div>
            <div className="flex flex-wrap gap-2">
              <Select
                value={v.status}
                onValueChange={(s) => onSetStatus(v.id, s)}
                disabled={busy === v.id}
              >
                <SelectTrigger
                  data-testid={`status-select-${v.name}`}
                  className="rounded-sm font-mono w-full"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((o) => (
                    <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        ))}
      </div>

      {/* Recent blocks pane */}
      <div className="border border-border rounded-sm bg-card overflow-hidden" data-testid="admin-recent-blocks">
        <div className="px-4 py-3 border-b border-border text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          Last 50 blocks · proposers + signatures
        </div>
        {recent.length === 0 ? (
          <div className="p-5 text-xs text-muted-foreground">No blocks yet.</div>
        ) : (
          <div className="divide-y divide-border max-h-[420px] overflow-y-auto">
            {recent.map((b) => (
              <div key={b.block_number} className="px-4 py-2 grid grid-cols-12 gap-2 items-center text-xs">
                <div className="col-span-2 font-mono">#{b.block_number}</div>
                <div className="col-span-3 text-[11px] text-muted-foreground">
                  {new Date(b.timestamp).toLocaleTimeString()}
                </div>
                <div className="col-span-3 font-mono text-[11px]">
                  {b.legacy_single_sealer ? "legacy single-sealer" : `proposer: ${b.proposer_id?.slice(0, 8) || "—"}…`}
                </div>
                <div className="col-span-2 font-mono text-[10px]">
                  {b.consensus_valid_weight != null ? `${b.consensus_valid_weight}/${b.consensus_threshold}` : "—"}
                </div>
                <div className="col-span-2 text-right">
                  {b.byzantine_detected && (
                    <span className="px-1 py-0.5 border border-status-tampered/40 bg-status-tampered/10 text-status-tampered rounded-sm text-[9px] uppercase tracking-widest">byzantine</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const StatusPill = ({ s }) => {
  const map = {
    online: "border-status-ok/40 bg-status-ok/10 text-status-ok",
    offline: "border-muted-foreground/30 bg-muted text-muted-foreground",
    byzantine: "border-status-tampered/40 bg-status-tampered/10 text-status-tampered",
  };
  return (
    <span className={`px-1.5 py-0.5 border rounded-sm text-[10px] font-mono uppercase tracking-widest ${map[s] || "border-border"}`}>
      {s}
    </span>
  );
};

function KV({ label, value, highlight, mini }) {
  const hl = highlight === "warn" ? "text-amber-600 dark:text-amber-400" : highlight === "ok" ? "text-status-ok" : "";
  return (
    <div className={mini ? "" : "border border-border rounded-sm bg-card px-3 py-2"}>
      <div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground mb-0.5">{label}</div>
      <div className={hl}>{value ?? "—"}</div>
    </div>
  );
}
